import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";

const { TextArea } = Input;

interface Stat {
  value?: string;
  label?: string;
}

interface FilingMilestoneData {
  heading?: string;
  description?: string;
  stats?: Stat[];
}

interface FilingMilestoneProps {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: any;
  milestoneData?: FilingMilestoneData;
  loading?: boolean;
}

const FilingMilestone: React.FC<FilingMilestoneProps> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  milestoneData,
  loading,
}) => {

  useEffect(() => {
    if (milestoneData) {
      form.setFieldsValue({
        milestone: {
          heading: milestoneData.heading || "",
          description: milestoneData.description || "",
          stats: milestoneData.stats || [],
        },
      });
    } else {
      form.resetFields();
    }
  }, [milestoneData, form]);

  return (
    <div>
      {!isEditing ? (
        <>
          {/* Desktop View */}

          <div className="rounded-[20px] overflow-hidden hidden sm:block">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Description</th>
                  <th className="py-3 px-4 text-center">Stats</th>
                  <th className="py-3 px-4 text-end">Action</th>
                </tr>
              </thead>

              <tbody>
                {milestoneData ? (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td className="py-3 px-4">
                      {milestoneData.heading || "—"}
                    </td>

                    <td className="py-3 px-4">
                      {milestoneData.description || "—"}
                    </td>

                    <td className="py-3 px-4 text-center">
                      {milestoneData.stats?.map((stat, i) => (
                        <div key={i}>
                          <strong>{stat.value}</strong> - {stat.label}
                        </div>
                      ))}
                    </td>

                    <td className="py-3 px-4 text-end text-primary">
                      <EditOutlined
                        onClick={() => setIsEditing(true)}
                        className="cursor-pointer text-[20px]"
                      />
                    </td>
                  </tr>
                ) : (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}

          <div className="sm:hidden">
            {milestoneData ? (
              <div className="bg-[#F9F9F9] border rounded-lg p-4 space-y-3">
                <div>
                  <span className="font-semibold text-primary block">
                    Heading
                  </span>
                  {milestoneData.heading}
                </div>

                <div>
                  <span className="font-semibold text-primary block">
                    Description
                  </span>
                  {milestoneData.description}
                </div>

                <div>
                  <span className="font-semibold text-primary block">
                    Stats
                  </span>
                  {milestoneData.stats?.map((stat, i) => (
                    <p key={i}>
                      <strong>{stat.value}</strong> - {stat.label}
                    </p>
                  ))}
                </div>

                <EditOutlined
                  onClick={() => setIsEditing(true)}
                  className="cursor-pointer text-[18px]"
                />
              </div>
            ) : (
              <div className="flex justify-center border rounded-lg p-4">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Edit Mode */

        <div>
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-lg sm:rounded-2xl shadow-sm">

            <Form layout="vertical" form={form}>

              <Form.Item
                label="Heading"
                name={["milestone", "heading"]}
                rules={[{ required: true, message: "Please enter heading" }]}
              >
                <Input placeholder="Enter heading" />
              </Form.Item>

              <Form.Item
                label="Description"
                name={["milestone", "description"]}
                rules={[{ required: true, message: "Please enter description" }]}
              >
                <TextArea rows={3} placeholder="Enter description" />
              </Form.Item>

              {/* Stats */}

              <Form.List name={["milestone", "stats"]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div
                        key={field.key}
                        className="flex gap-3 items-center mb-3"
                      >
                        <Form.Item
                          {...field}
                          name={[field.name, "value"]}
                          rules={[{ required: true }]}
                          className="flex-1"
                        >
                          <Input placeholder="Value (e.g. 10K+)" />
                        </Form.Item>

                        <Form.Item
                          {...field}
                          name={[field.name, "label"]}
                          rules={[{ required: true }]}
                          className="flex-1"
                        >
                          <Input placeholder="Description" />
                        </Form.Item>

                        <Button danger onClick={() => remove(field.name)}>
                          Remove
                        </Button>
                      </div>
                    ))}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add()}
                    >
                      Add Stat
                    </Button>
                  </>
                )}
              </Form.List>
            </Form>
          </div>

          <div className="flex gap-3 mt-6 sticky bottom-5">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>

            <ButtonWrapper onClick={handleCancel}>
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilingMilestone;